import { cloneElement, type ReactElement, type ReactNode } from 'react'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import * as S from './CustomizeTabs.styled'
import findIndex from 'lodash/findIndex'
import { useTranslation } from 'react-i18next'
import productTitle from 'utils/global/productTitle'

export type CustomizeTab = {
  // OrderProduct UUID when available; title is the fallback selection key.
  id?: string | null
  title: string
  // Customer-facing nickname distinguishing duplicate products in an order.
  nickname?: string | null
  content: ReactElement<{ isTransitioning?: boolean }>
}

type CustomizeTabsProps = {
  tabs?: CustomizeTab[]
  width?: string
  noOverflow?: boolean
  icon?: ReactNode
  selected?: [string | null, (id: string) => void]
  isTransitioning?: boolean
}

export const CustomizeTabs = ({
  tabs,
  width,
  noOverflow,
  icon,
  selected: [selected, setSelected] = ['', () => {}],
  isTransitioning = false,
}: CustomizeTabsProps) => {
  const { t } = useTranslation()

  // Support both id-based selection (for unique identification) and title-based (for backward compatibility)
  const selectedIndex = findIndex(tabs, (tab) => {
    // If tab has an id, use id for comparison
    if (tab.id) {
      return tab.id === selected
    }
    // Otherwise fall back to title comparison
    return tab.title === selected
  })

  // Count duplicate titles to add visual indicators
  const titleCounts: Record<string, number> = {}
  const titleIndexes: Record<string, number> = {}
  tabs?.forEach((tab) => {
    const baseTitle = tab.title.replace(' ✓', '').trim()
    titleCounts[baseTitle] = (titleCounts[baseTitle] || 0) + 1
  })

  return (
    <Flex full overflow="hidden">
      <Flex column margin="0 42px" width={width}>
        {tabs?.map(({ id, title, nickname }, i) => {
          const baseTitle = title.replace(' ✓', '').trim()
          const hasDuplicates = titleCounts[baseTitle] > 1
          const currentIndex = hasDuplicates
            ? (titleIndexes[baseTitle] = (titleIndexes[baseTitle] || 0) + 1)
            : 0
          const displayTitle = nickname
            ? `${title} (${nickname})`
            : hasDuplicates
              ? `${title} (${currentIndex})`
              : title

          return (
            <S.Tab
              clickable
              key={id || `${title}${i}`}
              data-testid="preview-product-tab"
              padding="16px"
              radius="2px"
              justify={icon ? 'space-between' : undefined}
              margin={
                title.includes('Front Insert') || title.includes('Extra')
                  ? '0 0 0 25px'
                  : undefined
              }
              selected={selectedIndex === i}
              onClick={() => setSelected(id || title)}
            >
              <Text>{productTitle(displayTitle, t)}</Text>
            </S.Tab>
          )
        })}
      </Flex>
      <Flex
        align="flex-start"
        column
        flex="1"
        overflow={!noOverflow ? 'auto' : 'inherit'}
      >
        {tabs?.map(({ id, title, content }, i) => (
          <Flex key={id || `${title}${i}`} column width="100%">
            {selectedIndex === i && (
              <Flex column width="100%">
                {cloneElement(content, { isTransitioning })}
              </Flex>
            )}
          </Flex>
        ))}
      </Flex>
    </Flex>
  )
}

export default CustomizeTabs
